/**
 * Map and filter the sessions-by-establishment stacked chart.
 */

import type { BarChartData } from '@/components/common/stats/HorizontalStackedBarChart';
import {
  CHART_FILTER_ELEMENT_IDS,
  SESSION_STATUS_KEY_TO_SERIES,
  type ChartFilterVisibility,
  type ChartView,
} from '@/components/charts/chart-configs';
import { chartEstablishmentCountLabel } from '@/utils/common-utils';
import { dashboardHasElement } from '@/utils/dashboard-ie-utils';

export { chartHeightForCount, hasChartSeriesData } from '@/utils/common-utils';

/** Toggle a session-status series in the stacked bar. */
export function toggleSessionStatus(current: string[], status: string): string[] {
  return current.includes(status) ? current.filter((key) => key !== status) : [...current, status];
}

/** Backend already returns top-N; hide unselected status series on the client. */
export function buildSessionsChartView(
  raw: BarChartData | null | undefined,
  sessionStatusFilter: string[],
): ChartView {
  if (!raw || raw.categories.length === 0) return { chartData: null, displayCount: 0 };

  const allowed = new Set(
    sessionStatusFilter.map((key) => SESSION_STATUS_KEY_TO_SERIES[key]).filter(Boolean),
  );
  const filtered: BarChartData = {
    categories: raw.categories,
    series: raw.series.filter((series) => allowed.has(series.name)),
  };
  return { chartData: filtered, displayCount: filtered.categories.length };
}

/** Subtitle count: "N établissement(s)", empty when there is nothing to plot. */
export function sessionsChartCountTitle(displayCount: number): string {
  if (displayCount <= 0) return '';
  return chartEstablishmentCountLabel(displayCount);
}

/** Which dashboard IE filters the sessions chart toolbar should show. */
export function resolveSessionsChartFilterVisibility(
  elements: { elementId: string }[],
): ChartFilterVisibility {
  const showPeriodFilter = dashboardHasElement(elements, CHART_FILTER_ELEMENT_IDS.period);
  const showDateRangeFilter = dashboardHasElement(elements, CHART_FILTER_ELEMENT_IDS.dateRange);
  const showEstablishmentFilter = dashboardHasElement(
    elements,
    CHART_FILTER_ELEMENT_IDS.establishment,
  );
  const showStatusFilter = dashboardHasElement(elements, CHART_FILTER_ELEMENT_IDS.status);
  return {
    showPeriodFilter,
    showDateRangeFilter,
    showEstablishmentFilter,
    showStatusFilter,
    hasAnyFilter:
      showPeriodFilter || showDateRangeFilter || showEstablishmentFilter || showStatusFilter,
  };
}
